import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { TankProvider } from './contexts/TankContext';
import { ProtectedLayout } from './components/layout/ProtectedLayout';
import { PublicToolsLayout } from './components/layout/PublicToolsLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { WelcomeWizard } from './features/onboarding';
import { LoginPage } from './pages/LoginPage';
import { ComingSoonPage } from './pages/ComingSoonPage';
import { CheckYourEmailPage } from './pages/CheckYourEmailPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import ChartsPage from './pages/ChartsPage';
import DataEditorPage from './pages/DataEditorPage';
import DosingPage from './pages/DosingPage';
import MaintenancePage from './pages/MaintenancePage';
import ICPTestsPage from './pages/ICPTestsPage';
import ICPTestDetailPage from './pages/ICPTestDetailPage';
import { SettingsPage } from './pages/SettingsPage';
import { TanksPage } from './pages/TanksPage';
import { DevicesPage } from './pages/DevicesPage';
import TraceDosingPage from './pages/TraceDosingPage';
import TraceDosingCalculatorPage from './pages/TraceDosingCalculatorPage';
import { DosingCalculatorPage } from './pages/tools/DosingCalculatorPage';
import { HttmSchedulerPage } from './pages/tools/HttmSchedulerPage';
import { DoseCalculatorPage } from './pages/tools/DoseCalculatorPage';
import { ToolsLandingPage } from './pages/tools/ToolsLandingPage';
import { LivestockPage } from './pages/LivestockPage';
import { PhotoInboxPage } from './pages/PhotoInboxPage';
import { TankShotsPage } from './pages/TankShotsPage';
import { TankShotsComparePage } from './pages/TankShotsComparePage';
import { SharedComparePage } from './pages/SharedComparePage';
import BulkImportPage from './features/livestock/import/BulkImportPage';
import { AssignPhotoView } from './features/livestock/AssignPhotoView';
import { OrganismDetailPage } from './pages/OrganismDetailPage';
import { OrganismComparePage } from './pages/OrganismComparePage';
import AnalysisPage from './pages/AnalysisPage';
import ParameterDetailPage from './pages/ParameterDetailPage';
import FishViewerPage from './pages/FishViewerPage';
import EquipmentPage from './pages/EquipmentPage';
import EquipmentViewerPage from './pages/EquipmentViewerPage';
import TasksPage from './pages/TasksPage';
import JournalPage from './pages/JournalPage';
import PowerPage from './pages/PowerPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { RouteErrorPage } from './pages/RouteErrorPage';

function RootLayout(): JSX.Element {
  return (
    <AuthProvider>
      <TankProvider>
        <Outlet />
      </TankProvider>
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  // A public share link (design 8.6), outside RootLayout: no AuthProvider or
  // TankProvider, so nothing on it calls apiClient (whose 401 goes to /login)
  // or reads the visitor's session. Matched case-insensitively (/S/ too).
  {
    element: <PublicToolsLayout sharePage />,
    errorElement: <RouteErrorPage />,
    children: [{ path: '/s/:token', element: <SharedComparePage /> }],
  },
  {
    element: <RootLayout />,
    // Replaces React Router's default error screen if a route throws
    errorElement: <RouteErrorPage />,
    children: [
      // Public routes - no layout
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <ComingSoonPage /> },
      { path: '/check-your-email', element: <CheckYourEmailPage /> },
      { path: '/verify-email', element: <VerifyEmailPage /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/reset-password', element: <ResetPasswordPage /> },

      // Public tools - shared header and tool navigation, no auth
      {
        element: <PublicToolsLayout />,
        children: [
          { path: '/tools', element: <ToolsLandingPage /> },
          { path: '/tools/dosing-calculator', element: <DosingCalculatorPage /> },
          { path: '/tools/httm-scheduler', element: <HttmSchedulerPage /> },
          { path: '/tools/dose-calculator', element: <DoseCalculatorPage /> },
        ],
      },

      // Onboarding wizard - authenticated but rendered WITHOUT the AppShell
      // chrome, as a sibling of the ProtectedLayout block. Kept outside
      // ProtectedLayout so the OnboardingGate mounted there never re-gates the
      // wizard into a redirect loop.
      {
        path: '/welcome',
        element: (
          <ProtectedRoute>
            <WelcomeWizard />
          </ProtectedRoute>
        ),
      },

      // Protected routes - shared layout with header and navbar
      {
        element: <ProtectedLayout />,
        children: [
          { path: '/', element: <ChartsPage /> },
          { path: '/charts', element: <ChartsPage /> },
          { path: '/data-editor', element: <DataEditorPage /> },
          { path: '/parameters/:parameterKey', element: <ParameterDetailPage /> },
          { path: '/dosing', element: <DosingPage /> },
          // Redirect old URL for bookmarks
          { path: '/dosetronic', element: <DosingPage /> },
          { path: '/trace-dosing', element: <TraceDosingPage /> },
          { path: '/trace-dosing/new', element: <TraceDosingCalculatorPage /> },
          { path: '/trace-dosing/:batchId/edit', element: <TraceDosingCalculatorPage /> },
          { path: '/maintenance', element: <MaintenancePage /> },
          { path: '/livestock/import', element: <BulkImportPage /> },
          { path: '/livestock', element: <LivestockPage /> },
          { path: '/livestock/photos', element: <PhotoInboxPage /> },
          // Its own route, so the back gesture returns to the grid
          { path: '/livestock/photos/:id', element: <AssignPhotoView /> },
          // Whole-tank photos; ?tank= selects the tank (design 6.2)
          { path: '/livestock/tank-shots', element: <TankShotsPage /> },
          { path: '/livestock/tank-shots/compare', element: <TankShotsComparePage /> },
          { path: '/tanks/:tankId/livestock/:livestockId', element: <OrganismDetailPage /> },
          // Compare one organism's photos (livestock compare design 8.2)
          {
            path: '/tanks/:tankId/livestock/:livestockId/compare',
            element: <OrganismComparePage />,
          },
          { path: '/analysis', element: <AnalysisPage /> },
          { path: '/icp-tests', element: <ICPTestsPage /> },
          { path: '/icp-tests/:barcode', element: <ICPTestDetailPage /> },
          { path: '/tanks', element: <TanksPage /> },
          { path: '/devices', element: <DevicesPage /> },
          { path: '/equipment', element: <EquipmentPage /> },
          { path: '/tasks', element: <TasksPage /> },
          { path: '/journal', element: <JournalPage /> },
          { path: '/power', element: <PowerPage /> },
          { path: '/settings', element: <SettingsPage /> },

          // Hidden admin pages - not in navigation
          { path: '/fish-viewer', element: <FishViewerPage /> },
          { path: '/equipment-viewer', element: <EquipmentViewerPage /> },
          { path: '/admin/users', element: <AdminUsersPage /> },
        ],
      },

      // Any URL not matched above. Public so logged-out visitors see the
      // 404 instead of being bounced to /login.
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);

function App(): JSX.Element {
  return <RouterProvider router={router} />;
}

export default App;
